import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";

interface ShareClipButtonProps {
  downloadUrl: string;
  clipName: string;
  className?: string;
  variant?: "icon" | "full";
}

const isMobileDevice = () => {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

export const ShareClipButton: React.FC<ShareClipButtonProps> = ({
  downloadUrl,
  clipName,
  className = "",
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isMobileDevice() && navigator.share) {
      setIsSharing(true);
      try {
        const response = await axios.get(downloadUrl, {
          responseType: "blob",
        });

        const urlPath = new URL(downloadUrl, window.location.href).pathname;
        const ext = (urlPath.match(/\.([a-zA-Z0-9]+)$/)?.[1] || "mp4").toLowerCase();
        const safeName = (clipName || "clip").replace(/[^a-zA-Z0-9-_.]+/g, "_");
        const fileName = `${safeName}.${ext}`;

        const file = new File([response.data], fileName, {
          type: response.data.type || "video/mp4",
        });

        await navigator.share({
          files: [file],
          title: clipName,
        });
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("Share failed:", error);
          toast({
            title: t("shareClip.shareFailed"),
            variant: "destructive",
          });
        }
      } finally {
        setIsSharing(false);
      }
    } else {
      setQrDialogOpen(true);
    }
  };

  const sharePageUrl = `${window.location.origin}/share?url=${encodeURIComponent(downloadUrl)}&name=${encodeURIComponent(clipName)}`;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`h-8 w-8 p-0 ${className}`}
            disabled={isSharing}
            onClick={handleShare}
          >
            {isSharing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Share2 className="w-3.5 h-3.5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={2}>
          {t("shareClip.share")}
        </TooltipContent>
      </Tooltip>

      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-sm" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{t("shareClip.scanToShare")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <p className="text-sm text-muted-foreground text-center">
              {t("shareClip.scanQrCode")}
            </p>
            <div className="bg-white p-4 rounded-lg">
              <QRCodeSVG
                value={sharePageUrl}
                size={200}
                level="M"
              />
            </div>
            <p className="text-xs text-muted-foreground text-center truncate max-w-full">
              {clipName}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareClipButton;
